import { Injectable } from '@angular/core';
import { AuthHttpService } from '@app/core/async-services/http/versioned/auth.http';
import { AuthUtilityService } from '@app/core/utils';
import {
  firstValueFrom,
  forkJoin,
  from,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { NavigationService } from '../navigation/navigation.service';
import { PendoService } from '../pendo.service';
import { PermissionService } from '../permissions/permission.service';
import { ServiceService } from '../service.service';
import { UserService } from '../user';
import { NotificationService } from '@app/modules/features/modules/communication/services/notification/notification.service';
import { Platform } from '@ionic/angular';
import { CookiesService } from '../cookies/cookies.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private authHttpService: AuthHttpService,
    private authUtilityService: AuthUtilityService,
    private navigationService: NavigationService,
    private pendoService: PendoService,
    private permissionService: PermissionService,
    private serviceService: ServiceService,
    private userService: UserService,
    private notificationService: NotificationService,
    private platform: Platform,
    private cookiesService: CookiesService,
  ) {}

  async isAuthenticated() {
    const token = await this.authUtilityService.getToken();
    return !!token;
  }

  async login(credentials: any) {
    const encryptionKeys = await this.getEncryptionKeys();
    const encrypted = this.authUtilityService.encrypt(
      JSON.stringify(credentials),
      encryptionKeys.public_key,
    );
    return firstValueFrom(this.authHttpService.login({ value: encrypted }));
  }

  forgotPassword(emailAddress: string) {
    return firstValueFrom(this.authHttpService.forgotPassword(emailAddress));
  }

  async confirmPasswordReset(confirmationObject: any) {
    const encryptionKeys = await this.getEncryptionKeys();
    const encrypted = this.authUtilityService.encrypt(
      JSON.stringify(confirmationObject),
      encryptionKeys.public_key,
    );
    return firstValueFrom(
      this.authHttpService.confirmForgotPassword({ value: encrypted }),
    );
  }

  async changePassword(form: any) {
    const encryptionKeys = await this.getEncryptionKeys();
    const encrypted = this.authUtilityService.encrypt(
      JSON.stringify(form),
      encryptionKeys.public_key,
    );
    return firstValueFrom(
      this.authHttpService.changePassword({ value: encrypted }),
    );
  }

  async getEncryptionKeys() {
    const data = await firstValueFrom(
      this.authHttpService.getPublicEncryptionKey(),
    );
    return {
      public_key: data.payload.public_key,
      key_id: data.payload.key_id,
    };
  }

  async clearAllSessionCookies() {
    await this.cookiesService.deleteAllCookies();
  }

  getAuthToken() {
    return this.authUtilityService.getToken();
  }

  setAuthtoken(token: string) {
    return this.authUtilityService.setToken(token);
  }

  initializeCoreData(): Observable<void | boolean> {
    return from(this.isAuthenticated()).pipe(
      switchMap((isAuthenticated) => {
        if (isAuthenticated) {
          return forkJoin([
            this.permissionService.fetchComponentPermissions(),
            this.permissionService.fetchCurrentUserPermissions(),
            this.serviceService.refreshServiceMap(),
            this.userService.fetchUser(),
          ]).pipe(
            switchMap(() => this.navigationService.initialize()),
            tap(() => {
              this.pendoService.identifyVisitor();
              if (this.platform.is('capacitor')) {
                this.notificationService.createBinding();
              }
            }),
          );
        } else return of(true);
      }),
    );
  }
}
